import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Input, Space, Tag, Typography } from "antd";
import WorkbenchService from "../../workbench/services/WorkbenchService";
import { WbBackendAssociation } from "../../workbench/types/WbTypes";
import { formatDateTime } from "../utils/workbenchNewSubjectView";
const service = new WorkbenchService();

export default function WorkbenchBackendAssociations({
    subjectId,
    onChange,
}: {
    subjectId: string;
    onChange: () => void;
}) {
    const generation = useRef(0);
    const [loadedSubject, setLoadedSubject] = useState("");
    const [items, setItems] = useState<WbBackendAssociation[]>([]);
    const [reason, setReason] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        let active = true;
        generation.current++;
        setLoadedSubject("");
        setItems([]);
        setReason({});
        setBusy(false);
        setFailed(false);
        service
            .fetchBackendAssociations(subjectId)
            .then((result) => {
                if (!active) return;
                if (result.isOk()) {
                    setItems(result.value);
                    setLoadedSubject(subjectId);
                } else setFailed(true);
            })
            .catch(() => {
                if (active) setFailed(true);
            });
        return () => {
            active = false;
            generation.current++;
        };
    }, [subjectId, revision]);
    async function withdraw(id: string) {
        const current = generation.current;
        setBusy(true);
        setFailed(false);
        try {
            const result = await service.withdrawBackendAssociation(
                subjectId,
                id,
                reason[id],
            );
            if (current !== generation.current) return;
            if (result.isOk()) {
                setRevision((x) => x + 1);
                onChange();
            } else setFailed(true);
        } catch {
            if (current === generation.current) setFailed(true);
        } finally {
            if (current === generation.current) setBusy(false);
        }
    }
    return (
        <details className="workbench-facts-associations">
            <summary>
                Backend associations{" "}
                <span>
                    {items.length
                        ? `${items.filter((item) => !item.withdrawnAt).length} active / ${items.length} total`
                        : loadedSubject === subjectId
                          ? "None established"
                          : failed
                            ? "Unavailable"
                            : "Loading..."}
                </span>
            </summary>
            <div className="workbench-facts-associations-body">
                <Typography.Paragraph>
                    Captured backend information is retained when Tasks move.
                    Remove an incorrect association to withdraw its support; the
                    backend record stays unchanged.
                </Typography.Paragraph>
                {failed && (
                    <Alert
                        type="error"
                        message="Backend associations could not be loaded or updated"
                    />
                )}
                {!items.length && !failed && (
                    <Typography.Text>
                        No established backend associations
                    </Typography.Text>
                )}
                {(loadedSubject === subjectId ? items : []).map((item) => (
                    <Card
                        key={item.id}
                        size="small"
                        className="workbench-facts-association"
                    >
                        <Space direction="vertical">
                            <Typography.Text>
                                {item.reference
                                    ? `${item.reference.system} · ${item.reference.kind} · ${item.reference.identifier}`
                                    : "Restricted source"}
                            </Typography.Text>
                            <Typography.Text>{item.sourceName}</Typography.Text>
                            <Typography.Text>
                                Captured:{" "}
                                {item.capturedAt
                                    ? formatDateTime(item.capturedAt)
                                    : "No successful capture"}
                            </Typography.Text>
                            {item.withdrawnAt ? (
                                <>
                                    <Tag>Association removed</Tag>
                                    <Typography.Text>
                                        {item.withdrawnBy} ·{" "}
                                        {formatDateTime(item.withdrawnAt)}
                                        {item.withdrawalReason
                                            ? ` · ${item.withdrawalReason}`
                                            : ""}
                                    </Typography.Text>
                                </>
                            ) : (
                                <>
                                    <Input
                                        aria-label={`Reason for removing association ${item.id}`}
                                        value={reason[item.id] ?? ""}
                                        onChange={(event) =>
                                            setReason({
                                                ...reason,
                                                [item.id]: event.target.value,
                                            })
                                        }
                                        placeholder="Reason for correction"
                                    />
                                    <Button
                                        danger
                                        disabled={
                                            busy || !reason[item.id]?.trim()
                                        }
                                        onClick={() => withdraw(item.id)}
                                    >
                                        Remove association
                                    </Button>
                                </>
                            )}
                        </Space>
                    </Card>
                ))}
            </div>
        </details>
    );
}
